import { OrganizationRoles } from '@/enums';
import { Organization } from '@/types';

/**
 * TELV1-568 — decide which organization and workspace to land a user in
 * after login, tolerating a landing organization that has become unusable.
 *
 * Why this exists as its own function rather than inline in the login flow:
 * the organization id and the workspace id must come from the SAME
 * organization. The previous inline code picked the organization from one
 * place and the workspace from another, and could emit a pair that did not
 * belong together. Returning both as one value makes that class of bug
 * unrepresentable rather than merely fixed.
 *
 * The escape-hatch requirement (TELV1-567): `last_organization` is persisted
 * server-side by the organization switcher BEFORE the target page renders,
 * so if that page then fails, the pointer is already written. Honouring it
 * unconditionally on every subsequent login pins the user into the broken
 * organization on every device, every browser and every incognito window,
 * with no client-side remedy. So the stored pointer is honoured only when
 * the organization it names is actually usable.
 */

/** A landing destination. Both ids always belong to the same organization. */
export interface LandingTarget {
  organizationId: string;
  workspaceId: string;
}

export type LandingResolution =
  | { ok: true; target: LandingTarget }
  | { ok: false; reason: 'NO_ORGANIZATION' | 'NO_WORKSPACE' };

function isMemberOf(organization: Organization | undefined, userId: string): boolean {
  return !!organization?.members?.some((member) => member?.user_id === userId);
}

/**
 * Usable = visible to this user, they belong to it, and it has a workspace
 * they can actually be dropped into. An organization with no `mainWorkspace`
 * resolves to a URL the dashboard cannot render, so it is not a destination.
 */
function isUsable(
  organization: Organization | undefined,
  userId: string,
): organization is Organization {
  return (
    !!organization &&
    !organization.isHidden &&
    !!organization.mainWorkspace &&
    isMemberOf(organization, userId)
  );
}

export function resolveLandingTarget(
  organizations: Organization[] | null | undefined,
  userId: string,
  lastOrganization?: string | null,
  lastWorkspace?: string | null,
): LandingResolution {
  const all = (organizations ?? []).filter(Boolean);

  const visibleMemberships = all.filter(
    (organization) => !organization.isHidden && isMemberOf(organization, userId),
  );

  // Nothing to land in at all — a different failure from "has orgs but none
  // usable", and it keeps the original, more accurate error message.
  if (visibleMemberships.length === 0) {
    return { ok: false, reason: 'NO_ORGANIZATION' };
  }

  // 1. The user's own stored landing spot, but only if it still works.
  const storedOrganization = lastOrganization
    ? all.find((organization) => organization._id === lastOrganization)
    : undefined;

  if (isUsable(storedOrganization, userId)) {
    return {
      ok: true,
      target: {
        organizationId: storedOrganization._id,
        // `last_workspace` belongs to this organization; when it is missing,
        // fall back to THIS organization's main workspace — never another's.
        workspaceId: lastWorkspace || storedOrganization.mainWorkspace,
      },
    };
  }

  // 2. Otherwise prefer an organization the user owns, matching the previous
  //    preference order, then any usable organization at all.
  const usable = visibleMemberships.filter((organization) => !!organization.mainWorkspace);

  const owned = usable.find((organization) =>
    organization.members.some(
      (member) => member?.user_id === userId && member?.role === OrganizationRoles.OWNER,
    ),
  );

  const fallback = owned ?? usable[0];

  // The user is a member of organizations, but not one of them has a
  // workspace for them. Previously this hard-failed even when only the
  // landing organization was affected; now it means genuinely nowhere to go.
  if (!fallback) {
    return { ok: false, reason: 'NO_WORKSPACE' };
  }

  return {
    ok: true,
    target: {
      organizationId: fallback._id,
      workspaceId: fallback.mainWorkspace,
    },
  };
}

export default resolveLandingTarget;
